/**
 * The checks the backend makes with shapely, run in the browser for inline feedback
 * (boundary-editor design §4.4). Quadratic in a polygon's vertices, which is fine for
 * drawn areas; the backend stays authoritative and catches what this misses (collinear
 * overlaps, touching edges).
 */
import { MAX_POLYGONS, MAX_VERTICES, vertexCount, type DrawnPolygon, type Ring } from './geometry';

export type Problem =
  | { polygon: number; code: 'self_intersection' | 'hole_outside' | 'overlap' }
  | { polygon: null; code: 'too_many_vertices' | 'too_many_polygons' };

type P = [number, number];
type Segment = [P, P];

const orient = (a: P, b: P, c: P) => Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
const cross = ([a, b]: Segment, [c, d]: Segment) => orient(a, b, c) * orient(a, b, d) < 0 && orient(c, d, a) * orient(c, d, b) < 0;
const segments = (r: Ring): Segment[] => r.map((p, i) => [p, r[(i + 1) % r.length]] as Segment);

function selfCrossing(r: Ring): boolean {
  const s = segments(r);
  for (let i = 0; i < s.length; i++) for (let j = i + 2; j < s.length; j++) if (!(i === 0 && j === s.length - 1) && cross(s[i], s[j])) return true;
  return false;
}
function ringsCross(a: Ring, b: Ring): boolean {
  const sb = segments(b);
  return segments(a).some((x) => sb.some((y) => cross(x, y)));
}
function inside(p: P, r: Ring): boolean {
  let hit = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i];
    const [xj, yj] = r[j];
    if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}
/** Inside the polygon's area: in the shell and in none of its holes. */
const inArea = (p: P, poly: DrawnPolygon) => inside(p, poly.shell) && !poly.holes.some((h) => inside(p, h));

export function validate(ps: DrawnPolygon[]): Problem[] {
  if (ps.length > MAX_POLYGONS) return [{ polygon: null, code: 'too_many_polygons' }];
  if (vertexCount(ps) > MAX_VERTICES) return [{ polygon: null, code: 'too_many_vertices' }];
  const problems: Problem[] = [];
  ps.forEach((poly, i) => {
    const rings = [poly.shell, ...poly.holes];
    if (rings.some(selfCrossing) || poly.holes.some((h, a) => poly.holes.some((k, b) => b > a && ringsCross(h, k)))) {
      problems.push({ polygon: i, code: 'self_intersection' });
    } else if (poly.holes.some((h) => ringsCross(h, poly.shell) || !inside(h[0], poly.shell))) {
      problems.push({ polygon: i, code: 'hole_outside' });
    }
    const overlaps = ps.slice(0, i).some((other) => {
      const otherRings = [other.shell, ...other.holes];
      return rings.some((r) => otherRings.some((o) => ringsCross(r, o))) || inArea(poly.shell[0], other) || inArea(other.shell[0], poly);
    });
    if (overlaps) problems.push({ polygon: i, code: 'overlap' });
  });
  return problems;
}
